import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  appServiceLines,
  commonEnums,
  configurationUtils,
  PatientEncounterDashboardColumns,
  PatientEncounterDashboardColumnsv2
} from '@codametrix/ui-common';
import { setPropertyConfig } from './ui-core.actions';

const fetchCaseListColumnConfiguration = createAsyncThunk<
  any,
  { tenantId: string; serviceLine: string },
  { rejectValue: CMxCommonApp.SubmitError }
>('FETCH_COLUMN_CONFIGURATION', async (params, _thunkAPI) => {
  const { tenantId, serviceLine } = params;
  const jsonValue = await configurationUtils.fetchMergedConfigurationJsonValue(
    'caselist-col-order',
    tenantId,
    serviceLine
  );

  // Only return entries where config.field is not null/undefined
  if (!jsonValue || typeof jsonValue !== 'object') {
    return {};
  }
  return Object.fromEntries(
    Object.entries(jsonValue).filter(
      ([, config]) => (config as { field?: string }).field != null
    )
  );
});

const fetchCaseDetailsLayoutConfiguration = createAsyncThunk<
  void,
  {
    tenantId: string;
    serviceLine: string;
    hasAmplifyCaseRunV2Access?: boolean;
  },
  { rejectValue: CMxCommonApp.SubmitError }
>('FETCH_CASE_DETAILS_LAYOUT_CONFIGURATION', async (params, { dispatch }) => {
  const { tenantId, serviceLine, hasAmplifyCaseRunV2Access = false } = params;
  const choiceName = serviceLine ?? '';

  const { ServiceLines } = commonEnums;

  let configName = choiceName;
  if (appServiceLines.isIPC(choiceName)) {
    configName = ServiceLines.IPC_LONG_NAME;
  }

  const slName: string = `${configName}`.toLowerCase().replaceAll(/\s/g, '');

  const encounterColumns = hasAmplifyCaseRunV2Access
    ? PatientEncounterDashboardColumnsv2
    : PatientEncounterDashboardColumns;

  // Check if slName exists in the chosen object - this is the fallback
  const fallbackPropertyConfig =
    slName in encounterColumns ? encounterColumns[slName] : null;

  try {
    let jsonValue = null;
    if (hasAmplifyCaseRunV2Access) {
      jsonValue = await configurationUtils.fetchMergedConfigurationJsonValue(
        'demographics-panel-layout',
        tenantId,
        serviceLine
      );
    } else {
      jsonValue = await configurationUtils.fetchMergedConfigurationJsonValue(
        'case-details-layout',
        tenantId,
        serviceLine
      );
    }

    if (jsonValue && Object.keys(jsonValue).length > 0) {
      // Convert structure to one the UI expects
      const layoutConfig: CMxCommonApp.PatientInformationConfiguration = {
        patient: Object.values(jsonValue.patient || {})
          .filter((value: any) => value !== null)
          .map((value: any) => {
            return {
              ...value
            };
          }),
        summary: Object.values(jsonValue.summary || {})
          .filter((value: any) => value !== null)
          .map((value: any) => {
            return {
              ...value
            };
          }),
        expanded: Object.values(jsonValue.expanded || {})
          .filter((value: any) => value !== null)
          .map((value: any) => {
            return {
              ...value
            };
          })
      };

      dispatch(setPropertyConfig(layoutConfig));
    } else if (fallbackPropertyConfig) {
      // Use fallback if no dynamic config found
      dispatch(setPropertyConfig(fallbackPropertyConfig));
    }
  } catch (error) {
    console.error('Error fetching case details layout configuration:', error);
    // Use fallback on error
    if (fallbackPropertyConfig) {
      dispatch(setPropertyConfig(fallbackPropertyConfig));
    }
  }
});

export {
  fetchCaseDetailsLayoutConfiguration,
  fetchCaseListColumnConfiguration
};
